import { ThreeEvent } from '@react-three/fiber';
import { useContextMenu } from 'components/common/context-menu-manager';
import {
  AirHandlerTypeCodeType,
  ConfigurationType,
  ContextMenuItemType,
  GridSpaceType,
  LevelType,
  LineSetType
} from 'data/types';
import { useDirectPointDrag } from 'hooks/use-direct-point-drag';
import { useLinesetActions } from 'hooks/use-lineset-actions';
import { useCallback, useMemo, useRef, useState } from 'react';
import { calculateLevelYOffsets, SCALE } from 'utils/3d';
import { LINESET_DEFAULT_COLOR } from 'utils/3d-lineset-color';
import {
  computeLineSetGeometry,
  isLinesetPointDragUnchanged,
  LinesetLevelRange,
  LinesetPointDragUpdate,
  LineSetPointGeometry,
  resolveLinesetPointDrag
} from 'utils/3d-lineset-geometry';
import { beginLinesetDrag, endLinesetDrag } from 'utils/lineset-drag-state';
import { linesetPerimetersByLevel } from 'utils/lineset-level-perimeters';
import { LinesetPerimetersByLevelType } from 'utils/lineset-level-perimeters-types';
import {
  linesetHasPointLevels,
  resolveLinesetPointLevels
} from 'utils/lineset-point-levels';
import { TransformControlsInteractionState } from 'utils/transform-controls';
import LineSet3D from './lineset-3d';
import LinesetPoint3D from './lineset-point-3d';
import LinesetPointGizmo from './lineset-point-gizmo';

type DragOverride = LinesetPointDragUpdate & { pointUniqueId: string };

type DraggableLinesetPointProps = {
  pointGeometry: LineSetPointGeometry;
  levelRanges: LinesetLevelRange[];
  perimetersByLevel: LinesetPerimetersByLevelType;
  opacity: number;
  color: number;
  selected: boolean;
  onClick: (event: ThreeEvent<MouseEvent>) => void;
  onContextMenu: (event: ThreeEvent<PointerEvent>) => void;
  onDragStart: () => void;
  onDragChange: (pointUniqueId: string, update: LinesetPointDragUpdate) => void;
  onDragEnd: (
    pointUniqueId: string,
    update: LinesetPointDragUpdate | null
  ) => void;
};

function DraggableLinesetPoint({
  pointGeometry,
  levelRanges,
  perimetersByLevel,
  opacity,
  color,
  selected,
  onClick,
  onContextMenu,
  onDragStart,
  onDragChange,
  onDragEnd
}: DraggableLinesetPointProps) {
  const pointGeometryRef = useRef(pointGeometry);
  const levelRangesRef = useRef(levelRanges);
  const perimetersByLevelRef = useRef(perimetersByLevel);
  const dragStartPointRef = useRef(pointGeometry.point);
  const dragStartLevelRef = useRef<string | undefined>(undefined);
  const gizmoInteractionRef = useRef<TransformControlsInteractionState>({
    hovered: false,
    dragging: false
  });

  pointGeometryRef.current = pointGeometry;
  levelRangesRef.current = levelRanges;
  perimetersByLevelRef.current = perimetersByLevel;

  const pointUniqueId = pointGeometry.point.uniqueId;

  const dragHandlers = useDirectPointDrag({
    pointPosition: [pointGeometry.x, pointGeometry.y, pointGeometry.z],
    isBlocked: () =>
      gizmoInteractionRef.current.hovered ||
      gizmoInteractionRef.current.dragging,
    onDragStart: () => {
      dragStartPointRef.current = pointGeometryRef.current.point;
      dragStartLevelRef.current =
        levelRangesRef.current[
          pointGeometryRef.current.levelIndex
        ]?.levelUniqueId;
      onDragStart();
    },
    onDragChange: (localPosition) => {
      onDragChange(
        pointUniqueId,
        resolveLinesetPointDrag(
          pointGeometryRef.current,
          localPosition,
          levelRangesRef.current,
          perimetersByLevelRef.current
        )
      );
    },
    onDragEnd: (localPosition) => {
      if (!localPosition) {
        onDragEnd(pointUniqueId, null);
        return;
      }
      const update = resolveLinesetPointDrag(
        pointGeometryRef.current,
        localPosition,
        levelRangesRef.current,
        perimetersByLevelRef.current
      );
      const unchanged = isLinesetPointDragUnchanged(
        dragStartPointRef.current,
        update,
        dragStartLevelRef.current
      );
      onDragEnd(pointUniqueId, unchanged ? null : update);
    }
  });

  if (selected) {
    return (
      <LinesetPointGizmo
        pointGeometry={pointGeometry}
        levelRanges={levelRanges}
        perimetersByLevel={perimetersByLevel}
        opacity={opacity}
        onDragStart={onDragStart}
        onDragChange={(update) => onDragChange(pointUniqueId, update)}
        onDragEnd={(update) => onDragEnd(pointUniqueId, update)}
        onContextMenu={onContextMenu}
        dragHandlers={dragHandlers}
        onInteractionChange={(state) => {
          gizmoInteractionRef.current = state;
        }}
      />
    );
  }

  return (
    <LinesetPoint3D
      position={[pointGeometry.x, pointGeometry.y, pointGeometry.z]}
      opacity={opacity}
      color={color}
      onClick={onClick}
      onContextMenu={onContextMenu}
      dragHandlers={dragHandlers}
    />
  );
}

type EditableLineSet3DProps = {
  lineset: LineSetType;
  configuration: ConfigurationType;
  configurationId: string;
  structure: LevelType[];
  opacity: number;
  color?: number;
  airHandlerTypeCode?: string;
  spaces?: GridSpaceType[];
  offsetX: number;
  offsetZ: number;
  selectedPointUniqueId: string | null;
  onSelectPoint: (pointUniqueId: string | null) => void;
  readOnly?: boolean;
};

export default function EditableLineSet3D({
  lineset,
  configuration,
  configurationId,
  structure,
  opacity,
  color = LINESET_DEFAULT_COLOR,
  airHandlerTypeCode,
  spaces = [],
  offsetX,
  offsetZ,
  selectedPointUniqueId,
  onSelectPoint,
  readOnly = false
}: EditableLineSet3DProps) {
  const { openContextMenu } = useContextMenu();
  const { addPoint, deletePoint, deleteLineset, movePoint } = useLinesetActions(
    configurationId,
    lineset.uniqueId
  );
  const [dragOverride, setDragOverride] = useState<DragOverride | null>(null);

  const editable = !readOnly && opacity >= 1;
  const dragId = `lineset-3d-${lineset.uniqueId}`;
  const perimetersByLevel = useMemo(
    () => linesetPerimetersByLevel(structure),
    [structure]
  );

  const displayLineset = useMemo(() => {
    if (!dragOverride) return lineset;
    const materializedLevels =
      dragOverride.levelUniqueId !== undefined &&
      !linesetHasPointLevels(lineset)
        ? resolveLinesetPointLevels(lineset)
        : null;
    return {
      ...lineset,
      points: lineset.points.map((point, index) => {
        const basePoint = materializedLevels
          ? { ...point, levelUniqueId: materializedLevels[index] }
          : point;
        if (point.uniqueId !== dragOverride.pointUniqueId) return basePoint;
        const overridden = {
          ...basePoint,
          positionX: dragOverride.positionX,
          positionY: dragOverride.positionY
        };
        if (dragOverride.positionZ !== undefined) {
          overridden.positionZ = dragOverride.positionZ;
        }
        if (dragOverride.levelUniqueId !== undefined) {
          overridden.levelUniqueId = dragOverride.levelUniqueId;
        }
        return overridden;
      })
    };
  }, [lineset, dragOverride]);

  const effectiveYOffset = useMemo(() => {
    const pointLevels = resolveLinesetPointLevels(displayLineset);
    const distinctLevels = new Set(pointLevels.filter(Boolean));
    const isMultiLevel =
      displayLineset.isCrossFloor === true || distinctLevels.size > 1;
    if (isMultiLevel) return 0;

    const levelYOffsets = calculateLevelYOffsets(structure);
    const condenserLevelIndex = Math.max(
      structure.findIndex(
        (level) => level.uniqueId === displayLineset.condenserLevelUniqueId
      ),
      0
    );
    return levelYOffsets[condenserLevelIndex]?.yOffset ?? 0;
  }, [displayLineset, structure]);

  const geometry = useMemo(
    () =>
      computeLineSetGeometry({
        lineset: displayLineset,
        configuration,
        structure,
        yOffset: effectiveYOffset,
        airHandlerTypeCode: airHandlerTypeCode as
          | AirHandlerTypeCodeType
          | undefined,
        spaces
      }),
    [
      displayLineset,
      configuration,
      structure,
      effectiveYOffset,
      airHandlerTypeCode,
      spaces
    ]
  );

  const handlePointClick = useCallback(
    (event: ThreeEvent<MouseEvent>, pointUniqueId: string) => {
      if (event.delta > 5) return;
      event.stopPropagation();
      onSelectPoint(pointUniqueId);
    },
    [onSelectPoint]
  );

  const handlePointContextMenu = useCallback(
    (event: ThreeEvent<PointerEvent>, pointUniqueId: string) => {
      event.stopPropagation();
      event.nativeEvent.preventDefault();

      const menuItems: ContextMenuItemType[] = [];

      menuItems.push({
        label: 'Delete Point',
        disabled: lineset.points.length <= 2,
        onClick: () => {
          if (lineset.points.length <= 2) return;
          if (selectedPointUniqueId === pointUniqueId) {
            onSelectPoint(null);
          }
          deletePoint(pointUniqueId);
        }
      });

      menuItems.push({
        label: 'Delete Line Set',
        onClick: () => {
          if (selectedPointUniqueId) {
            onSelectPoint(null);
          }
          deleteLineset();
        }
      });

      openContextMenu(
        event.nativeEvent.clientX,
        event.nativeEvent.clientY,
        menuItems,
        `lineset-3d-point-${pointUniqueId}`
      );
    },
    [
      lineset.points.length,
      selectedPointUniqueId,
      onSelectPoint,
      deletePoint,
      deleteLineset,
      openContextMenu
    ]
  );

  const handleSegmentContextMenu = useCallback(
    (event: ThreeEvent<PointerEvent>, segmentIndex: number) => {
      const clickedLinesetPoint = event.intersections.some(
        (intersection) => intersection.object.userData?.isLinesetPoint === true
      );
      if (clickedLinesetPoint) return;

      event.stopPropagation();
      event.nativeEvent.preventDefault();

      const gridX = (event.point.x - offsetX) / SCALE;
      const gridY = (event.point.z - offsetZ) / SCALE;

      const startPoint = displayLineset.points[segmentIndex];
      const endPoint = displayLineset.points[segmentIndex + 1];

      let interpolatedZ: number | undefined;
      if (
        startPoint?.positionZ !== null &&
        startPoint?.positionZ !== undefined &&
        endPoint?.positionZ !== null &&
        endPoint?.positionZ !== undefined
      ) {
        const dx = endPoint.positionX - startPoint.positionX;
        const dy = endPoint.positionY - startPoint.positionY;
        const lengthSquared = dx * dx + dy * dy;
        const t =
          lengthSquared > 0
            ? Math.min(
                Math.max(
                  ((gridX - startPoint.positionX) * dx +
                    (gridY - startPoint.positionY) * dy) /
                    lengthSquared,
                  0
                ),
                1
              )
            : 0;
        interpolatedZ = Math.round(
          startPoint.positionZ + (endPoint.positionZ - startPoint.positionZ) * t
        );
      }

      const menuItems: ContextMenuItemType[] = [
        {
          label: 'Add Point',
          onClick: () => {
            addPoint(segmentIndex, gridX, gridY, interpolatedZ);
          }
        },
        {
          label: 'Delete Line Set',
          onClick: () => {
            if (selectedPointUniqueId) {
              onSelectPoint(null);
            }
            deleteLineset();
          }
        }
      ];

      openContextMenu(
        event.nativeEvent.clientX,
        event.nativeEvent.clientY,
        menuItems,
        `lineset-3d-segment-${lineset.uniqueId}-${segmentIndex}`
      );
    },
    [
      displayLineset,
      offsetX,
      offsetZ,
      lineset.uniqueId,
      selectedPointUniqueId,
      onSelectPoint,
      addPoint,
      deleteLineset,
      openContextMenu
    ]
  );

  const handleDragStart = useCallback(() => {
    beginLinesetDrag(dragId);
  }, [dragId]);

  const handleDragChange = useCallback(
    (pointUniqueId: string, update: LinesetPointDragUpdate) => {
      setDragOverride({ pointUniqueId, ...update });
    },
    []
  );

  const handleDragEnd = useCallback(
    (pointUniqueId: string, update: LinesetPointDragUpdate | null) => {
      if (update) {
        movePoint(pointUniqueId, update);
      }
      setDragOverride(null);
      setTimeout(() => {
        endLinesetDrag(dragId);
      }, 0);
    },
    [movePoint, dragId]
  );

  return (
    <group>
      <LineSet3D
        lineset={displayLineset}
        configuration={configuration}
        structure={structure}
        yOffset={effectiveYOffset}
        opacity={opacity}
        color={color}
        airHandlerTypeCode={airHandlerTypeCode}
        spaces={spaces}
        interactive={editable}
        onSegmentContextMenu={editable ? handleSegmentContextMenu : undefined}
      />
      {editable &&
        geometry.points.map((pointGeometry) => {
          const pointUniqueId = pointGeometry.point.uniqueId;
          const isConnectionAnchor =
            pointGeometry.index === 0 ||
            pointGeometry.index === geometry.points.length - 1;

          if (isConnectionAnchor) {
            return (
              <LinesetPoint3D
                key={pointUniqueId}
                position={[pointGeometry.x, pointGeometry.y, pointGeometry.z]}
                opacity={opacity}
                color={color}
                onContextMenu={(event) =>
                  handlePointContextMenu(event, pointUniqueId)
                }
              />
            );
          }

          return (
            <DraggableLinesetPoint
              key={pointUniqueId}
              pointGeometry={pointGeometry}
              levelRanges={geometry.levelRanges}
              perimetersByLevel={perimetersByLevel}
              opacity={opacity}
              color={color}
              selected={selectedPointUniqueId === pointUniqueId}
              onClick={(event) => handlePointClick(event, pointUniqueId)}
              onContextMenu={(event) =>
                handlePointContextMenu(event, pointUniqueId)
              }
              onDragStart={handleDragStart}
              onDragChange={handleDragChange}
              onDragEnd={handleDragEnd}
            />
          );
        })}
    </group>
  );
}
